import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTestimonials } from '../../context/TestimonialContext';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../supabase/supabase';
import Button from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { FiYoutube, FiMessageSquare, FiLink, FiCopy, FiRefreshCw, FiCheckCircle, FiStar, FiAward, FiBriefcase, FiGlobe, FiActivity } from 'react-icons/fi';

const InputCard = ({ icon, title, description, children, className = '', onSync, isSyncing, isSyncSuccess, comingSoon }) => (
  <div className={`p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10 hover:border-orange-500/30 transition-all duration-300 flex flex-col h-full relative ${className}`}>
    {comingSoon && (
      <div className="absolute top-6 right-6 z-20">
        <span className="px-3 py-1 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-full text-[10px] font-bold tracking-wider uppercase shadow-sm">
          Coming Soon
        </span>
      </div>
    )}
    
    <div className="flex items-start gap-4 mb-6 pr-12">
      <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0 border border-orange-500/20">
        <SafeIcon icon={icon} className="text-orange-400 text-lg" />
      </div>
      <div>
        <h3 className="text-lg font-semibold text-amber-50 mb-1">{title}</h3>
        <p className="text-sm text-zinc-400 leading-relaxed">{description}</p>
      </div>
    </div>

    {onSync && (
      <div className={`absolute right-6 z-20 flex flex-col items-end ${comingSoon ? 'top-16' : 'top-6'}`}>
        <button
          onClick={comingSoon ? undefined : onSync}
          disabled={comingSoon || isSyncing || isSyncSuccess}
          className={`p-2.5 rounded-xl border transition-all shadow-[0_0_15px_rgba(234,88,12,0.1)] disabled:opacity-80
            ${comingSoon
              ? 'bg-zinc-800/50 border-zinc-700 text-zinc-500 cursor-not-allowed shadow-none'
              : isSyncSuccess
              ? 'bg-green-500/10 border-green-500/30 text-green-400 cursor-default shadow-[0_0_15px_rgba(34,197,94,0.1)]'
              : 'bg-orange-500/10 hover:bg-orange-500/20 hover:shadow-[0_0_20px_rgba(234,88,12,0.2)] text-orange-400 border-orange-500/20'
            }`}
          title={comingSoon ? 'Coming soon' : 'Sync Reviews'}
        >
          {isSyncSuccess ? (
            <SafeIcon icon={FiCheckCircle} />
          ) : (
            <SafeIcon icon={FiRefreshCw} className={isSyncing ? "animate-spin" : ""} />
          )}
        </button>

        <AnimatePresence>
          {isSyncSuccess && (
            <motion.div
              initial={{ opacity: 0, y: 5, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 5, scale: 0.95 }}
              className="absolute top-full mt-3 right-0 w-56 p-3 bg-zinc-900/95 border border-green-500/30 rounded-xl text-xs text-green-400 text-center backdrop-blur-xl shadow-[0_10px_30px_rgba(0,0,0,0.5)] origin-top-right"
            >
              <div className="font-semibold mb-1 text-green-300">Sync Initiated!</div>
              <div className="text-zinc-400 text-[11px] leading-relaxed">
                Check the Reviews tab. Your testimonials will magically appear in a few minutes.
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    )}

    <div className="pl-14 mt-auto">
      {children}
    </div>
  </div>
);

const PLATFORM_LABELS = {
  youtube: 'YouTube',
  google: 'Google',
  trustpilot: 'Trustpilot',
  clutch: 'Clutch',
  g2: 'G2',
  referral: 'Referral'
};

const STATUS_STYLES = {
  running: { dot: 'bg-orange-400 animate-pulse', text: 'text-orange-400', label: 'running' },
  success: { dot: 'bg-green-400', text: 'text-green-400', label: 'success' },
  partial: { dot: 'bg-amber-400', text: 'text-amber-400', label: 'partial' },
  error: { dot: 'bg-red-400', text: 'text-red-400', label: 'error' },
  stalled: { dot: 'bg-zinc-500', text: 'text-zinc-500', label: 'no response' }
};

// A run that n8n never closed out. Nothing will ever update the row, so the
// UI ages it out rather than showing an eternal spinner.
const STALLED_AFTER_MS = 15 * 60 * 1000;

const resolveStatus = (log) =>
  log.status === 'running' && Date.now() - new Date(log.created_at).getTime() > STALLED_AFTER_MS
    ? 'stalled'
    : log.status;

const formatLogTime = (iso) => {
  const then = new Date(iso);
  const diffMins = Math.round((Date.now() - then.getTime()) / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 60 * 24) return `${Math.round(diffMins / 60)}h ago`;
  return then.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const SyncActivity = ({ userId, refreshKey }) => {
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;

    let cancelled = false;
    let timer = null;

    const loadLogs = async () => {
      const { data, error } = await supabase
        .from('sync_logs')
        .select('id, platform, status, message, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (cancelled) return;
      if (error) console.error('Error loading sync logs:', error);

      const rows = data || [];
      setLogs(rows);
      setIsLoading(false);

      // Only keep polling while a run is genuinely in flight. n8n closes the
      // row out minutes after the click, so we watch for it — but once every
      // run is terminal (or aged out to "stalled"), we stop and make no
      // further requests until the user acts or reloads.
      const hasPending = rows.some((r) => resolveStatus(r) === 'running');
      if (hasPending) timer = setTimeout(loadLogs, 15000);
    };

    loadLogs();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [userId, refreshKey]);

  return (
    <div className="p-6 rounded-2xl bg-zinc-900/40 backdrop-blur-md border border-orange-500/10">
      <div className="flex items-start gap-4 mb-6">
        <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center flex-shrink-0 border border-orange-500/20">
          <SafeIcon icon={FiActivity} className="text-orange-400 text-lg" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-amber-50 mb-1">Sync Activity</h3>
          <p className="text-sm text-zinc-400 leading-relaxed">
            Your last 10 sync runs. Imports happen in the background, so results appear here as they finish.
          </p>
        </div>
      </div>

      <div className="pl-14">
        {isLoading ? (
          <div className="py-6 flex justify-center">
            <div className="w-6 h-6 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
          </div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-zinc-500 py-4">
            No syncs yet. Hit the sync button on any connected platform above to get started.
          </p>
        ) : (
          <ul className="divide-y divide-orange-500/10">
            {logs.map((log) => {
              const resolved = resolveStatus(log);
              const style = STATUS_STYLES[resolved] || STATUS_STYLES.error;
              const message = resolved === 'stalled'
                ? "This sync didn't report back. It may still be running — if nothing appears, try again or contact support."
                : log.message;
              return (
                <li key={log.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-1.5 ${style.dot}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-sm font-medium text-amber-50">
                        {PLATFORM_LABELS[log.platform] || log.platform}
                      </span>
                      <span className={`text-[11px] uppercase tracking-wider font-semibold ${style.text}`}>
                        {style.label}
                      </span>
                      <span className="text-[11px] text-zinc-600 ml-auto flex-shrink-0">
                        {formatLogTime(log.created_at)}
                      </span>
                    </div>
                    {message && (
                      <p className="text-sm text-zinc-400 leading-relaxed mt-0.5 break-words">
                        {message}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const Connect = () => {
  const { user } = useAuth();
  const { fetchTestimonials } = useTestimonials();
  
  const [isLoading, setIsLoading] = useState(true);
  const [syncingPlatform, setSyncingPlatform] = useState(null);
  const [syncSuccessPlatform, setSyncSuccessPlatform] = useState(null);
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [copiedTxt, setCopiedTxt] = useState(false);
  const [isVerifyingDomain, setIsVerifyingDomain] = useState(false);
  const [syncLogRefreshKey, setSyncLogRefreshKey] = useState(0);

  const [formData, setFormData] = useState({
    youtube: '',
    domain: '',
    domainTxt: '',
    isDomainVerified: false,
    hasSeoDomain: false
  });

  // Load saved integration data on mount
  useEffect(() => {
    if (!user?.id) return;

    const loadIntegrations = async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;

        if (!accessToken) {
          throw new Error('You must be logged in to load domain verification.');
        }

        const [integRes, seoRes, verifyRes] = await Promise.all([
          supabase.from('user_integrations').select('youtube_playlist_url').eq('user_id', user.id).maybeSingle(),
          supabase.from('seo_settings').select('company_url').eq('user_id', user.id).maybeSingle(),
          supabase.functions.invoke('verify-domain', {
            body: { action: 'get_token' },
            headers: { Authorization: `Bearer ${accessToken}` }
          })
        ]);

        const verifyData = verifyRes.data || {};
        if (verifyRes.error) console.error("Edge function error:", verifyRes.error);
        if (verifyData.error) console.error("Edge function returned error:", verifyData.error);

        const txt = verifyData.domain_verification_txt || '';
        const isVerified = verifyData.is_domain_verified || false;
        const vDomain = verifyData.verified_domain || '';
        const seoDomain = seoRes.data?.company_url || '';

        const initialDomain = isVerified ? vDomain : (seoDomain || vDomain || '');

        setFormData({
          youtube: integRes.data?.youtube_playlist_url || '',
          domain: initialDomain,
          domainTxt: txt,
          isDomainVerified: isVerified,
          hasSeoDomain: !!seoDomain
        });
      } catch (err) {
        console.error('Error loading integrations:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadIntegrations();
  }, [user?.id]);

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText('https://api.momentoftruth.io/v1/send/review');
    setCopiedWebhook(true);
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  const handleCopyTxt = () => {
    navigator.clipboard.writeText(formData.domainTxt);
    setCopiedTxt(true);
    setTimeout(() => setCopiedTxt(false), 2000);
  };

  const handleVerifyDomain = async () => {
    if (!formData.domain) {
      alert('Please enter a domain to verify (e.g., moment.reviews)');
      return;
    }
    
    setIsVerifyingDomain(true);
    
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const { data, error } = await supabase.functions.invoke('verify-domain', {
        body: { action: 'verify', domain: formData.domain },
        headers: { Authorization: `Bearer ${accessToken}` }
      });

      if (error) {
        // Fallback catch for function execution errors
        throw error;
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      if (data?.success) {
        setFormData(prev => ({
          ...prev,
          domain: data.verified_domain,
          isDomainVerified: true
        }));
        alert('Domain verified successfully. Business badge activation will be handled securely.');
      }
    } catch (err) {
      console.error('Verification error:', err);
      // Ensure the error message string is displayed correctly
      const errorMsg = typeof err.message === 'string' ? err.message : 'Error checking DNS records. Please try again.';
      alert(errorMsg);
    } finally {
      setIsVerifyingDomain(false);
    }
  };

  const handleIndividualSync = async (platform) => {
    if (!formData.isDomainVerified) {
      alert('Please verify your domain before syncing reviews.');
      return;
    }

    setSyncingPlatform(platform);
    setSyncSuccessPlatform(null); // Reset success state

    try {
      // Save youtube playlist URL before syncing if it's the youtube platform
      if (platform === 'youtube') {
        await supabase.from('user_integrations').upsert({
          user_id: user.id,
          youtube_playlist_url: formData.youtube,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });
      }

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.access_token) {
        throw new Error('You must be logged in to sync reviews.');
      }

      const payload = {
        event: 'fetch_reviews',
        platform: platform
      };

      const { error: proxyError } = await supabase.functions.invoke('n8n-proxy', {
        body: payload,
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (proxyError) {
        // supabase-js masks non-2xx responses as a generic message and hides
        // the real error (cooldown 429, validation 400, etc.) in the Response
        // body on .context. Surface that so the user sees why nothing synced.
        let serverMessage = '';
        try {
          const body = await proxyError.context?.json?.();
          serverMessage = body?.error || '';
        } catch { /* body wasn't JSON; fall through to generic */ }
        throw new Error(serverMessage || proxyError.message);
      }

      // Show success state
      setSyncSuccessPlatform(platform);
      setSyncLogRefreshKey((k) => k + 1);
      setTimeout(() => setSyncSuccessPlatform(null), 6000); // Hide after 6 seconds

      // Refresh local state to show any new reviews that might have processed instantly
      await fetchTestimonials();

    } catch (err) {
      console.error('Sync error:', err);
      alert(err.message || `Failed to sync ${platform} reviews.`);
    } finally {
      setSyncingPlatform(null);
    }
  };

  const inputStyles = "w-full bg-[#0a0500]/50 border border-orange-500/20 rounded-xl px-4 py-3 text-zinc-200 placeholder-zinc-600 focus:border-orange-500/50 focus:ring-1 focus:ring-orange-500/50 transition-all outline-none";

  if (isLoading) {
    return (
      <div className="py-20 flex justify-center w-full">
        <div className="w-10 h-10 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto pb-12"
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Domain Verification Card */}
        <div className="lg:col-span-2">
          <InputCard 
            icon={FiGlobe} 
            title="Domain Verification" 
            description="Verify your domain ownership to enable custom domain hosting for your public page and advanced widget features."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-xs text-zinc-500 font-semibold tracking-wider uppercase mb-2 block">Domain Name</label>
                <input 
                  type="text" 
                  placeholder="e.g. acme.com" 
                  className={`${inputStyles} ${(formData.isDomainVerified || formData.hasSeoDomain) ? 'opacity-50 cursor-not-allowed' : ''}`}
                  value={formData.domain}
                  onChange={(e) => setFormData({...formData, domain: e.target.value, isDomainVerified: false})}
                  disabled={formData.isDomainVerified || formData.hasSeoDomain}
                />
                {!formData.isDomainVerified && formData.hasSeoDomain && (
                  <p className="text-[11px] text-orange-400 mt-2">Domain is synced from your SEO settings.</p>
                )}
              </div>
              
              <div>
                <label className="text-xs text-zinc-500 font-semibold tracking-wider uppercase mb-2 block">TXT Record Value</label>
                <div className="relative">
                  <input 
                    type="text" 
                    readOnly
                    value={formData.domainTxt}
                    className={`${inputStyles} pr-12 bg-zinc-950/80 text-zinc-500 border-zinc-800`}
                  />
                  <button 
                    onClick={handleCopyTxt}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-orange-400 transition-colors p-1"
                    title="Copy to clipboard"
                  >
                    <SafeIcon icon={copiedTxt ? FiCheckCircle : FiCopy} className={copiedTxt ? "text-green-400" : ""} />
                  </button>
                </div>
                <p className="text-[11px] text-zinc-500 mt-2">Add this as a TXT record in your DNS provider (e.g. Cloudflare, GoDaddy).</p>
              </div>
            </div>

            <div className="mt-5 flex items-center justify-between border-t border-orange-500/10 pt-5">
              {formData.isDomainVerified ? (
                <div className="flex items-center gap-2 text-green-400 text-sm font-medium bg-green-500/10 px-4 py-2 rounded-lg border border-green-500/20 shadow-[0_0_15px_rgba(34,197,94,0.1)]">
                  <SafeIcon icon={FiCheckCircle} /> Domain Verified
                </div>
              ) : (
                <Button 
                  onClick={handleVerifyDomain} 
                  disabled={isVerifyingDomain || !formData.domain}
                  variant="secondary"
                  className="!py-2 !px-6"
                >
                  {isVerifyingDomain ? (
                    <><div className="w-4 h-4 border-2 border-orange-400/30 border-t-orange-400 rounded-full animate-spin"></div> Verifying...</>
                  ) : "Verify DNS Record"}
                </Button>
              )}
            </div>
          </InputCard>
        </div>

        <InputCard 
          icon={FiYoutube} 
          title="Youtube Testimonials" 
          description="Add your testimonials in a youtube playlist and add here."
          onSync={() => handleIndividualSync('youtube')}
          isSyncing={syncingPlatform === 'youtube'}
          isSyncSuccess={syncSuccessPlatform === 'youtube'}
        >
          <input 
            type="text" 
            placeholder="e.g. PLx0sYbCqOb8TBPRdmBHs5Iftvv9TPboYG" 
            className={inputStyles}
            value={formData.youtube}
            onChange={(e) => setFormData({...formData, youtube: e.target.value})}
          />
        </InputCard>

        <InputCard 
          icon={FiMessageSquare} 
          title="Google Reviews" 
          description="Automatically sync your Google Business reviews. Essential for local SEO and building neighborhood trust."
          onSync={() => handleIndividualSync('google')}
          isSyncing={syncingPlatform === 'google'}
          isSyncSuccess={syncSuccessPlatform === 'google'}
        >
          <input 
            type="text" 
            disabled
            value={formData.isDomainVerified ? formData.domain : 'Verify domain first'}
            className={`${inputStyles} opacity-50 cursor-not-allowed`}
          />
        </InputCard>

        <InputCard 
          icon={FiStar} 
          title="Trustpilot" 
          description="Sync your Trustpilot reviews. A proven way to validate your brand promise to new customers."
          onSync={() => handleIndividualSync('trustpilot')}
          isSyncing={syncingPlatform === 'trustpilot'}
          isSyncSuccess={syncSuccessPlatform === 'trustpilot'}
        >
          <input 
            type="text" 
            disabled
            value={formData.isDomainVerified ? formData.domain : 'Verify domain first'}
            className={`${inputStyles} opacity-50 cursor-not-allowed`}
          />
        </InputCard>

        <InputCard
          icon={FiBriefcase}
          title="Clutch"
          description="For Service Providers: Sync your Clutch reviews to establish industry authority."
          onSync={() => handleIndividualSync('clutch')}
          isSyncing={syncingPlatform === 'clutch'}
          isSyncSuccess={syncSuccessPlatform === 'clutch'}
        >
           <input
            type="text"
            disabled
            value={formData.isDomainVerified ? formData.domain : 'Verify domain first'}
            className={`${inputStyles} opacity-50 cursor-not-allowed`}
          />
        </InputCard>

        {/* G2 — no sync button: the lane isn't built (see COMING SOON) */}
        <InputCard
          icon={FiAward}
          title="G2"
          description="For B2B Software: Sync your G2 reviews to prove your product delivers on its promises."
          comingSoon={true}
        >
           <input
            type="text"
            disabled
            value={formData.isDomainVerified ? formData.domain : 'Verify domain first'}
            className={`${inputStyles} opacity-50 cursor-not-allowed`}
          />
        </InputCard>

        {/* Webhooks Card */}
        <InputCard 
          icon={FiLink} 
          title="Incoming Webhooks" 
          description="Send testimonials using API."
          comingSoon={true}
        >
          <div className="relative opacity-60 pointer-events-none">
            <input 
              type="text" 
              readOnly
              value="https://api.momentoftruth.io/v1/send/review" 
              className={`${inputStyles} pr-12 bg-zinc-950/80 text-zinc-500 border-zinc-800`}
            />
            <button className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 p-1">
              <SafeIcon icon={FiCopy} />
            </button>
          </div>
        </InputCard>

        {/* Sync Activity Log */}
        <div className="lg:col-span-2">
          <SyncActivity userId={user?.id} refreshKey={syncLogRefreshKey} />
        </div>

      </div>
    </motion.div>
  );
};

export default Connect;